import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { getStockOrderDetail } from '../../../services/stockOrders';
import { IStockOrderDetail, SHIPMENT_STATUS_STOCK_ORDER } from '../../../types/stockOrders';
import Tab1General from './components/Tab1General/Tab1General';
import Tab2Products from './components/Tab2Products/Tab2Products';
import Tab3Billing from './components/Tab3Billing/Tab3Billing';
import ChargerModule from '../../../components/chargerModule';
import BordOneToneIcon from '../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import CancelStockOrderModal from './components/CancelStockOrderModal/CancelStockOrderModal';
import StockOrderDetailSkeleton from './components/StockOrderDetailSkeleton/StockOrderDetailSkeleton';
import CommentsInboxTrigger from './components/CommentsInboxTrigger/CommentsInboxTrigger';
import BordStatus from 'components/NudosComponents/BordStatus/BordStatus';
import { STATUS_VARIANTS } from 'components/NudosComponents/BordStatus/BordStatus.type';
import './StockOrderDetail.scss';

const StockOrderDetail = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { replace } = useHistory();
  const [selectedTabId, setSelectedTabId] = useState(0);
  const [headerDetail, setHeaderDetail] = useState<HTMLElement | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data: stockOrderData, mutate: updateStockOrderData } = useSWR<IStockOrderDetail>(
    ['order-stock-detail', uuid],
    () => getStockOrderDetail(uuid)
  );

  const updateWithoutLoaders = async () => {
    await updateStockOrderData();
  };

  const returnAction = () => replace('/stock/list-stock');

  const renderVariantStatus = {
    [SHIPMENT_STATUS_STOCK_ORDER.CONFIRMED]: STATUS_VARIANTS.default,
    [SHIPMENT_STATUS_STOCK_ORDER.DELIVERY_PROCESS]: STATUS_VARIANTS.informative,
    [SHIPMENT_STATUS_STOCK_ORDER.DELAYED]: STATUS_VARIANTS.danger,
    [SHIPMENT_STATUS_STOCK_ORDER.DELIVERED]: STATUS_VARIANTS.success,
    [SHIPMENT_STATUS_STOCK_ORDER.CANCELLED]: STATUS_VARIANTS.danger
  };

  const renderLabelStatus = {
    [SHIPMENT_STATUS_STOCK_ORDER.CONFIRMED]: 'Confirmado',
    [SHIPMENT_STATUS_STOCK_ORDER.DELIVERY_PROCESS]: 'En proceso',
    [SHIPMENT_STATUS_STOCK_ORDER.DELAYED]: 'Atrasado',
    [SHIPMENT_STATUS_STOCK_ORDER.DELIVERED]: 'Entregado',
    [SHIPMENT_STATUS_STOCK_ORDER.CANCELLED]: 'Cancelado'
  };

  const stockOrderTabsList = [
    {
      tabId: 0,
      tabName: 'General',
      tabComponent: stockOrderData ? (
        <Tab1General
          stockOrderData={stockOrderData}
          updateWithoutLoaders={updateWithoutLoaders}
          setSelectedTabId={setSelectedTabId}
        />
      ) : null
    },
    {
      tabId: 1,
      tabName: 'Lista de productos',
      tabComponent: stockOrderData ? (
        <Tab2Products stockOrderData={stockOrderData} updateWithoutLoaders={updateWithoutLoaders} />
      ) : null
    },
    {
      tabId: 2,
      tabName: 'Facturación',
      tabComponent: stockOrderData ? (
        <Tab3Billing stockOrderData={stockOrderData} updateWithoutLoaders={updateWithoutLoaders} />
      ) : null
    }
  ];

  useEffect(() => {
    const header = document.getElementById('headerDetail');
    if (header) setHeaderDetail(header);
  }, []);

  return (
    <div className="stockOrderDetail">
      <ChargerModule infoSideBar={{ position: 'stock' }}>
        <div className="stockOrderDetailContainer">
          <section id="headerDetail" className="headerDetail">
            <div className="leftSection">
              <div className="flex items-center justify-between">
                <div className="returnArrow" onClick={returnAction}>
                  <BordOneToneIcon variant="arrowHeadLeft" standardSize={20} />
                </div>
                <BordStatus
                  label={renderLabelStatus[stockOrderData?.status as keyof typeof renderLabelStatus]}
                  variant={renderVariantStatus[stockOrderData?.status as keyof typeof renderVariantStatus]}
                />
              </div>
              {!stockOrderData ? (
                <div className="stockOrderReferenceAndShipmentsCount">
                  <div className="skeletonReference animate-pulse" />
                  <div className="skeletonShipmentsCount animate-pulse" />
                </div>
              ) : (
                <>
                  <div className="stockOrderReferenceAndShipmentsCount">
                    <div className="stockOrderReference">Orden {stockOrderData?.orderId}</div>
                    <div className="stockOrderShipmentsCount">
                      Productos - {stockOrderData?.generalData?.shipments?.length || '- Sin información'}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="tabsContainer">
                      {stockOrderTabsList.map(({ tabId, tabName }) => (
                        <button
                          key={tabId}
                          onClick={() => setSelectedTabId(tabId)}
                          className={`tab ${selectedTabId === tabId ? 'active' : ''}`}
                        >
                          {tabName}
                        </button>
                      ))}
                    </div>
                    {stockOrderData.status !== SHIPMENT_STATUS_STOCK_ORDER.CANCELLED &&
                      stockOrderData.status !== SHIPMENT_STATUS_STOCK_ORDER.DELIVERED && (
                        <button className="cancelOrder" onClick={() => setShowCancelModal(true)} disabled={isLoading}>
                          <span>Cancelar orden de compra</span>
                          <BordOneToneIcon variant="x" standardSize={14} />
                        </button>
                      )}
                  </div>
                </>
              )}
            </div>
          </section>

          {!stockOrderData ? (
            <StockOrderDetailSkeleton />
          ) : (
            <section
              className="tabContent"
              style={{ height: `calc(100% - ${headerDetail?.clientHeight}px)`, overflow: 'auto' }}
            >
              {stockOrderTabsList[selectedTabId]?.tabComponent}
            </section>
          )}

          {stockOrderData && <CommentsInboxTrigger stockOrderData={stockOrderData} uuid={uuid} />}
        </div>
      </ChargerModule>
      {showCancelModal && stockOrderData && (
        <CancelStockOrderModal
          closeModal={() => setShowCancelModal(false)}
          stockOrderData={stockOrderData}
          onSuccess={() => {
            updateStockOrderData();
            replace('/stock/list-stock');
          }}
          loading={isLoading}
          setIsLoading={setIsLoading}
        />
      )}
    </div>
  );
};

export default StockOrderDetail;
